import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { getCurrentTimeUTC } from 'shared/api';
import { useGameStore, useGlobalStore } from 'shared/store';
import { DateTime, Settings } from 'luxon';
import { AppUser } from 'shared/types';

type InitCooldownParams = {
  nextAvailableStart: AppUser['next_available_start'];
  // lastStart: string;
  // waitingTime: number;
  // _isDayCooldown: boolean;
};

export const useGameCooldownTimer = () => {
  const settings = useGameStore((state) => state.settings);
  const setGameReady = useGameStore((state) => state.setGameReady);
  const gameReady = useGameStore((state) => state.gameReady);
  const user = useGlobalStore((state) => state.user);
  const updateUserStore = useGlobalStore((state) => state.updateUser);
  const appVisible = useGlobalStore((state) => state.appVisible);

  const [gameCooldown, setGameCooldown] = useState<number | null>(null);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const isDayCooldown = user.current_limit >= user.max_daily_limit;
  const [loading, setloading] = useState(true);

  const initCooldown = useCallback(
    async ({ nextAvailableStart }: InitCooldownParams) => {
      if (!nextAvailableStart) {
        setloading(false);
        setGameCooldown(0);
        return;
      }

      const nextAvailableDate = DateTime.fromISO(nextAvailableStart);
      const { seconds: differenceInSeconds } = nextAvailableDate.diffNow('seconds');
      const seconds = differenceInSeconds > 0 ? differenceInSeconds : 0;
      setGameCooldown(seconds);
    },
    [setGameCooldown],
  );

  useEffect(() => {
    if (typeof gameCooldown === 'number' && gameCooldown < 1) {
      interval.current && clearInterval(interval.current);
      setGameCooldown(0);
      setGameReady(true);
      isDayCooldown && updateUserStore({ current_limit: 0 });
    }
  }, [setGameReady, updateUserStore, isDayCooldown, gameCooldown]);

  useEffect(() => {
    const effect = async () => {
      try {
        if (!appVisible) {
          interval.current && clearInterval(interval.current);
          return;
        }
        if (gameReady) {
          interval.current && clearInterval(interval.current);
          setGameCooldown(0);
          setloading(false);
          return;
        }

        await initCooldown({
          nextAvailableStart: user.next_available_start,
        });

        interval.current = setInterval(() => {
          setGameCooldown((prevCooldown) => {
            return prevCooldown ? prevCooldown - 1 : 0;
          });
        }, 1000);
      } catch (error) {
        console.log(error);
      }
    };
    effect();

    return () => {
      interval.current && clearInterval(interval.current);
    };
  }, [initCooldown, gameReady, user.next_available_start, appVisible]);

  return { gameCooldown, loading, isDayCooldown };
};
